import { useState, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import { useAnafStatus } from './useAnafStatus';

/**
 * Custom hook for submitting invoices to ANAF e-Factura
 *
 * API environment (production/sandbox) is controlled by backend configuration
 * via Key Vault secret ANAF-USE-TEST-API.
 *
 * Provides:
 * - Single invoice submission
 * - Batch invoice submission
 * - Status checking
 * - Loading/error state management
 *
 * @returns {Object} ANAF submission functions and state
 */
export function useAnafSubmit() {
  const anafStatus = useAnafStatus();

  const [submitting, setSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { current, total, results }
  const [lastError, setLastError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  /**
   * Check if ANAF submission is ready (user has valid token)
   * @returns {Object} { ready: boolean, reason: string }
   */
  const checkAnafReady = useCallback(() => {
    if (anafStatus.loading) {
      return { ready: false, reason: 'Se verifică statusul ANAF...' };
    }

    if (anafStatus.error) {
      return { ready: false, reason: `Eroare ANAF: ${anafStatus.error}` };
    }

    if (!anafStatus.hasToken) {
      return { ready: false, reason: 'Nu sunteți autentificat în ANAF. Click pe butonul ANAF pentru autentificare.' };
    }

    if (!anafStatus.isActive) {
      return { ready: false, reason: 'Token-ul ANAF nu este activ. Reautentificați-vă.' };
    }

    if (anafStatus.isExpired) {
      return { ready: false, reason: 'Token-ul ANAF a expirat. Reautentificați-vă.' };
    }

    return { ready: true, reason: null };
  }, [anafStatus]);

  /**
   * Submit a single invoice to ANAF e-Factura
   *
   * @param {number} invoiceId - Invoice ID to submit
   * @param {Object} options - Optional submission options
   * @param {boolean} options.forceStandardInvoiceCode - If true, use 380 instead of 751 for BON_FISCAL
   * @returns {Promise<Object>} { success, data, error }
   */
  const submitToAnaf = useCallback(async (invoiceId, options = {}) => {
    const readyCheck = checkAnafReady();
    if (!readyCheck.ready) {
      setLastError(readyCheck.reason);
      return { success: false, data: null, error: readyCheck.reason };
    }

    setSubmitting(true);
    setLastError(null);
    setLastResult(null);

    try {
      const response = await apiFetch(`/api/anaf/invoices/submit/${invoiceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          force_standard_invoice_code: options.forceStandardInvoiceCode || false
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || data.error || `Eroare ${response.status}`;
        setLastError(errorMsg);
        return { success: false, data: null, error: errorMsg };
      }

      if (data.success) {
        setLastResult(data);
        return {
          success: true,
          data: {
            uploadId: data.upload_id,
            status: data.status,
            message: data.message,
            submittedAt: data.submitted_at
          },
          error: null
        };
      } else {
        const errorMsg = data.error || 'Trimitere eșuată';
        setLastError(errorMsg);
        return { success: false, data: null, error: errorMsg };
      }

    } catch (err) {
      const errorMsg = err.message || 'Eroare de rețea la trimiterea în ANAF';
      setLastError(errorMsg);
      return { success: false, data: null, error: errorMsg };
    } finally {
      setSubmitting(false);
    }
  }, [checkAnafReady]);

  /**
   * Submit multiple invoices to ANAF e-Factura in batch
   *
   * @param {number[]} invoiceIds - Array of invoice IDs to submit
   * @returns {Promise<Object>} { success, data, error }
   */
  const batchSubmitToAnaf = useCallback(async (invoiceIds) => {
    const readyCheck = checkAnafReady();
    if (!readyCheck.ready) {
      setLastError(readyCheck.reason);
      return { success: false, data: null, error: readyCheck.reason };
    }

    if (!invoiceIds || invoiceIds.length === 0) {
      const errorMsg = 'Nu există facturi pentru trimitere';
      setLastError(errorMsg);
      return { success: false, data: null, error: errorMsg };
    }

    setSubmitting(true);
    setLastError(null);
    setLastResult(null);
    setBatchProgress({ current: 0, total: invoiceIds.length, results: [] });

    try {
      const response = await apiFetch('/api/anaf/invoices/batch-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_ids: invoiceIds })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || data.error || `Eroare ${response.status}`;
        setLastError(errorMsg);
        setBatchProgress(null);
        return { success: false, data: null, error: errorMsg };
      }

      // Update progress with final results
      setBatchProgress({
        current: data.total,
        total: data.total,
        results: data.results || []
      });

      setLastResult(data);

      return {
        success: data.success_count > 0,
        data: {
          total: data.total,
          successCount: data.success_count,
          failedCount: data.failed_count,
          results: data.results
        },
        error: data.failed_count > 0 ? `${data.failed_count} facturi au eșuat` : null
      };

    } catch (err) {
      const errorMsg = err.message || 'Eroare de rețea la trimiterea în ANAF';
      setLastError(errorMsg);
      setBatchProgress(null);
      return { success: false, data: null, error: errorMsg };
    } finally {
      setSubmitting(false);
    }
  }, [checkAnafReady]);

  /**
   * Check invoice status at ANAF
   *
   * @param {number} invoiceId - Invoice ID to check
   * @returns {Promise<Object>} { success, data, error }
   */
  const checkInvoiceStatus = useCallback(async (invoiceId) => {
    const readyCheck = checkAnafReady();
    if (!readyCheck.ready) {
      return { success: false, data: null, error: readyCheck.reason };
    }

    try {
      const response = await apiFetch(`/api/anaf/invoices/status/${invoiceId}`, {
        method: 'GET'
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          data: null,
          error: data.message || data.error || `Eroare ${response.status}`
        };
      }

      return {
        success: true,
        data: {
          uploadId: data.upload_id,
          status: data.status,
          message: data.message,
          submittedAt: data.submitted_at,
          checkedAt: data.checked_at
        },
        error: null
      };

    } catch (err) {
      return {
        success: false,
        data: null,
        error: err.message || 'Eroare la verificarea statusului'
      };
    }
  }, [checkAnafReady]);

  /**
   * Clear any stored errors/results
   */
  const clearState = useCallback(() => {
    setLastError(null);
    setLastResult(null);
    setBatchProgress(null);
  }, []);

  return {
    // State
    submitting,
    batchProgress,
    lastError,
    lastResult,

    // ANAF status (from useAnafStatus)
    anafStatus: {
      loading: anafStatus.loading,
      hasToken: anafStatus.hasToken,
      isActive: anafStatus.isActive,
      isExpired: anafStatus.isExpired,
      isExpiringSoon: anafStatus.isExpiringSoon,
      certificateHolder: anafStatus.certificateHolder,
      daysUntilExpiry: anafStatus.daysUntilExpiry
    },

    // Functions
    checkAnafReady,
    submitToAnaf,
    batchSubmitToAnaf,
    checkInvoiceStatus,
    clearState,
    refreshAnafStatus: anafStatus.refresh
  };
}
